import { ReactElement, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import CustomTextField from 'src/components/forms/theme-elements/CustomTextField';
import CustomFormLabel from 'src/components/forms/theme-elements/CustomFormLabel';
import { ActionButton, DS } from 'src/components/shared';
import { changePassword, getMe } from 'src/services/userService';
import { setMustChangePassword } from 'src/store/apps/auth/AuthSlice';
import { useDispatch, useSelector } from 'src/store/Store';

const validationSchema = Yup.object({
  newUsername: Yup.string().max(255),
  currentPassword: Yup.string().required('La contraseña actual es requerida'),
  newPassword: Yup.string()
    .required('La nueva contraseña es requerida')
    .min(8, 'Debe tener al menos 8 caracteres')
    .max(255),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Las contraseñas no coinciden')
    .required('Confirmá la nueva contraseña'),
});

// Bloquea el resto de la aplicación hasta que un usuario con contraseña
// temporal (mustChangePassword) la reemplace por una definitiva. Se monta
// una única vez alrededor del layout autenticado.
const RequirePasswordChangeGate = ({ children }: { children: ReactElement }) => {
  const dispatch = useDispatch();
  const accessToken = useSelector((state) => state.auth.accessToken);
  const mustChangePassword = useSelector((state) => state.auth.mustChangePassword);

  // El estado en memoria no sobrevive a un refresh de página, así que
  // reconsultamos el flag al montar para no dejar pasar a alguien que
  // todavía tiene una contraseña temporal pendiente de cambiar.
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!accessToken) {
      setChecked(true);

      return;
    }

    getMe()
      .then((me) => dispatch(setMustChangePassword(me.mustChangePassword)))
      .catch(() => undefined)
      .finally(() => setChecked(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const formik = useFormik({
    initialValues: {
      newUsername: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await changePassword({
          oldPassword: values.currentPassword,
          newPassword: values.newPassword,
          newUsername: values.newUsername || undefined,
        });
        dispatch(setMustChangePassword(false));
        Swal.fire({
          icon: 'success',
          title: '¡Listo!',
          text: 'Tu contraseña se actualizó correctamente.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: DS.blue,
        });
      } catch (error: any) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error?.message ?? 'No se pudo actualizar la contraseña.',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: DS.error,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (!checked) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!mustChangePassword) return children;

  return (
    <Dialog open fullWidth maxWidth="sm" disableEscapeKeyDown PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 18, color: DS.textPrimary, borderBottom: `1px solid ${DS.border}`, pb: 2 }}>
        Cambiá tu contraseña
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography fontSize={13} color={DS.textSecondary} mb={2}>
            Por seguridad, tenés que establecer una nueva contraseña antes de continuar. También podés elegir tu
            nombre de usuario.
          </Typography>
          <Stack spacing={2.5}>
            <Box>
              <CustomFormLabel>Nombre de usuario</CustomFormLabel>
              <CustomTextField
                fullWidth
                id="newUsername"
                name="newUsername"
                placeholder="Dejar en blanco para mantener el actual"
                value={formik.values.newUsername}
                onChange={formik.handleChange}
                error={formik.touched.newUsername && Boolean(formik.errors.newUsername)}
                helperText={formik.touched.newUsername && formik.errors.newUsername}
              />
            </Box>
            <Box>
              <CustomFormLabel>Contraseña temporal actual</CustomFormLabel>
              <CustomTextField
                fullWidth
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                error={formik.touched.currentPassword && Boolean(formik.errors.currentPassword)}
                helperText={formik.touched.currentPassword && formik.errors.currentPassword}
              />
            </Box>
            <Box>
              <CustomFormLabel>Nueva contraseña</CustomFormLabel>
              <CustomTextField
                fullWidth
                type="password"
                id="newPassword"
                name="newPassword"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                helperText={formik.touched.newPassword && formik.errors.newPassword}
              />
            </Box>
            <Box>
              <CustomFormLabel>Confirmar nueva contraseña</CustomFormLabel>
              <CustomTextField
                fullWidth
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${DS.border}`, gap: 1 }}>
          <ActionButton type="submit" disabled={formik.isSubmitting}>
            Guardar y continuar
          </ActionButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RequirePasswordChangeGate;
